import type { ImageType } from '@eliadmin/core/src/api/api-types';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { stripTags } from '@eliadmin/core/src/utils/dlz/text';

const isSvg = (image: ImageType) => image.url.split('?')[0].toLowerCase().endsWith('.svg');

/** Template placeholder shown in a photo slot until the photo is uploaded in the administration. */
const PLACEHOLDER = '/images/placeholder.png';

type CmsImageProps = {
  image: ImageType | null;
  /** Photo slots show the placeholder while empty; decorations pass `false` and stay empty. */
  fallback?: boolean;
  /** Fallback alt when the media has no title (usually the item name, HTML allowed). */
  alt?: string;
  /** Image next to text that already names it (card titles, links): `alt=""`. */
  decorative?: boolean;
  sizes: string;
  /** Above-the-fold image (LCP). */
  preload?: boolean;
  className?: string;
};

/** Admin image covering its positioned parent; the placeholder until the image is uploaded. */
export function CmsImage({
  image,
  fallback = true,
  alt,
  decorative,
  sizes,
  preload,
  className,
}: CmsImageProps) {
  if (!image) {
    return fallback ? (
      <Image
        src={PLACEHOLDER}
        alt=""
        fill
        sizes={sizes}
        unoptimized
        className={cn(className, 'object-cover')}
      />
    ) : null;
  }

  return (
    <Image
      src={image.url}
      alt={decorative ? '' : resolveImageAlt(image, stripTags(alt ?? ''))}
      fill
      sizes={sizes}
      preload={preload}
      fetchPriority={preload ? 'high' : undefined}
      unoptimized={isSvg(image)}
      className={cn('object-cover', className)}
    />
  );
}

/**
 * Content icon from the administration (categories, benefits, stores). The box keeps its Figma size
 * even before the icon is uploaded, so the layout does not move when it arrives.
 */
export function CmsIcon({ image, className }: { image: ImageType | null; className?: string }) {
  return (
    <span aria-hidden="true" className={cn('relative block shrink-0', className)}>
      {image ? (
        <Image
          src={image.url}
          alt=""
          fill
          sizes="48px"
          unoptimized={isSvg(image)}
          className="object-contain"
        />
      ) : null}
    </span>
  );
}
